import type {
  AdminDocumentaryResourceVM,
  CreateDocumentaryResourceBody,
  UpdateDocumentaryResourceBody,
} from "@shared-kernel/domain/types/documentary-resource";
import type {
  AdminRepository,
  AdminPendingActionsVM,
  AdminBatchListItem,
  AdminStudioListItem,
  CreateAdminStudioBody,
  UpdateAdminStudioBody,
  ExternalProviderBatchRequestVM,
  EditAndApproveExternalProviderBatchRequestBody,
  RejectExternalProviderBatchRequestBody,
  ApproveExternalProviderPaymentBody,
  ExternalProviderDepositPreviewVM,
  ExternalProviderDepositTransactionRef,
  RefuseExternalProviderPaymentBody,
  ApproveBatchParticipationPaymentBody,
  RefuseBatchParticipationPaymentBody,
  LessonPaginationParams,
  AdminLessonListVM,
  AdminGlobalSearchVM,
  AdminCreateInternalTrainingBody,
  AdminUpdateInternalTrainingBody,
  CreateBalanceAdjustmentBody,
  AddTimelineEventBody,
  UpdateTimelineEventBody,
  CreateUserBody,
  UpdateRolesBody,
  TrainingFollowUpWithUrgenciesVM,
  TrainingFollowUpAttendanceSheetVM,
  TrainingFollowUpTrainingItemVM,
  AdminTrainingFollowUpVM,
  TrainingInvoice,
  TrainingInvoiceListPaginationParams,
  ImpersonateResponseVM,
  ImpersonationCandidateVM,
  AdminVM,
  UserVM,
  UserDetailVM,
  AdminStudentVM,
  StudentListElementVM,
  AdminTeacherVM,
  TeacherListElementVM,
  AdminProviderVM,
  ProviderListItemVM,
  UninvitedUserVM,
  UserInvitationVM,
  UpdateUserAccountBody,
  UpdateStudentSettingsBody,
  UpdateTeacherSettingsBody,
  UpdateProviderSettingsBody,
  StudentSummaryVM,
  FundingRequestVM,
  ApprovedFundingRequestDetailVM,
  FundingRequestListItemVM,
  ConventionPaymentVM,
  LessonListVM,
  StandardPayment,
  UpdatePaymentBody,
  TeacherLessonListVM,
  ReviewFundingRequestBody,
  UpdateFundingRequestBody,
  ApproveFundingRequestBody,
  FundingListPaginationParams,
  FundingListItemVM,
  ValidateTeacherPayoutBody,
  TeacherPayoutListPaginationParams,
  AdminTeacherPayoutVM,
  AdminBatchDetailVM,
  BatchPaymentVM,
  InternalTrainingVM,
  AdminBalanceAdjustmentListVM,
  RefundVM,
  UpdateRefundBody,
  TimelineEventVM,
  TrainingFollowUpPaginationParams,
} from "@shared-kernel/application/ports/admin/admin-repository";
import type { FundingRequestEligibilityVM } from "@shared-kernel/application/ports/student/student-repository";
import type { EligibleTrainingItemsVM } from "@academy-context/shared/domain/types/eligible-training-items";
import type { BATCH_ORGANIZER_TYPE } from "@academy-context/shared/domain/types/enums/batch-organizer-type";

import { HttpGateway } from "@shared-kernel/secondary/http-gateway";
import type { TokenStorageGateway } from "@shared-kernel/application/ports/token-storage-gateway";
import backendConfig from "@config/backend";
import type { FUNDING_REQUEST_STATUS } from "@academy-context/read/domain/types/enums/funding-request-status";
import type { FUNDING_REQUEST_ERROR_REASONS } from "@academy-context/read/domain/types/admin/funding-request";
import type { TRAINING_ITEM_TYPE } from "@academy-context/shared/domain/types/training-items";
import type { PaginatedResult } from "@shared-kernel/domain/types/paginated-result";
import type { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import type { BatchEnrollmentCandidateVM } from "@shared-kernel/application/ports/admin/batch-enrollment-candidate";

export class BackendAdminRepository extends HttpGateway implements AdminRepository {
  private _route: string = "v1/admin";
  private _adminFundingRequestRoute: string = "v1/admin/funding-requests";

  constructor(tokenStorageGateway: TokenStorageGateway) {
    super(backendConfig.url, tokenStorageGateway);
  }

  // ========================================
  // Pending Actions
  // ========================================

  async hasPendingActions(): Promise<AdminPendingActionsVM> {
    const response = await this._instance.get<AdminPendingActionsVM>(`${this._route}/has-pending-actions`);
    return response.data;
  }

  // ========================================
  // External Provider Batch Requests
  // ========================================

  async listExternalProviderBatchRequests(): Promise<ExternalProviderBatchRequestVM[]> {
    const { data: requests } = await this._instance.get(`${this._route}/external-provider-batch-requests`);
    return requests;
  }

  async getExternalProviderBatchRequest(requestId: string): Promise<ExternalProviderBatchRequestVM> {
    const { data } = await this._instance.get<ExternalProviderBatchRequestVM>(
      `${this._route}/external-provider-batch-requests/${requestId}`,
    );
    return data;
  }

  async editAndApproveExternalProviderBatchRequest(body: EditAndApproveExternalProviderBatchRequestBody): Promise<void> {
    const { requestId, ...payload } = body;
    await this._instance.post(`${this._route}/external-provider-batch-requests/${requestId}:approve`, payload);
  }

  async rejectExternalProviderBatchRequest(body: RejectExternalProviderBatchRequestBody): Promise<void> {
    await this._instance.post(`${this._route}/external-provider-batch-requests/${body.requestId}:reject`, {
      adminNote: body.adminNote,
    });
  }

  // ========================================
  // External Provider Payments
  // ========================================

  async approveExternalProviderPayment(body: ApproveExternalProviderPaymentBody): Promise<void> {
    await this._instance.post(`${this._route}/external-providers/payments/${body.paymentId}:approve`, {
      totalPaid: body.totalPaid,
      adminNote: body.adminNote,
      paymentDate: body.paymentDate,
      paymentReference: body.paymentReference,
      correctedInvoice: body.correctedInvoice,
    });
  }

  async previewExternalProviderDeposit(body: ExternalProviderDepositTransactionRef): Promise<ExternalProviderDepositPreviewVM> {
    const { data } = await this._instance.get<ExternalProviderDepositPreviewVM>(
      `${this._route}/external-providers/participations/${body.participationId}/deposits:preview`,
      { params: { transactionId: body.transactionId } },
    );
    return data;
  }

  async recordExternalProviderDeposit(body: ExternalProviderDepositTransactionRef): Promise<void> {
    await this._instance.post(`${this._route}/external-providers/participations/${body.participationId}/deposits`, {
      transactionId: body.transactionId,
    });
  }

  async refuseExternalProviderPayment(body: RefuseExternalProviderPaymentBody): Promise<void> {
    await this._instance.post(`${this._route}/external-providers/payments/${body.paymentId}:refuse`, {
      adminNote: body.adminNote,
    });
  }

  // ========================================
  // Batches
  // ========================================

  async listBatchPayments(organizerType: BATCH_ORGANIZER_TYPE): Promise<BatchPaymentVM[]> {
    const { data } = await this._instance.get<BatchPaymentVM[]>(`${this._route}/batch-payments`, {
      params: { organizerType },
    });
    return data;
  }

  async listAllBatches(organizerType?: BATCH_ORGANIZER_TYPE): Promise<AdminBatchListItem[]> {
    const { data } = await this._instance.get<AdminBatchListItem[]>(`${this._route}/batches`, {
      params: organizerType ? { organizerType } : undefined,
    });
    return data;
  }

  async getBatchDetail(id: string): Promise<AdminBatchDetailVM> {
    const { data } = await this._instance.get<AdminBatchDetailVM>(`${this._route}/batches/${id}`);
    return data;
  }

  async checkBatchParticipationCompletion(batchId: string): Promise<void> {
    await this._instance.post(`${this._route}/batches/${batchId}/participations:check-completion`);
  }

  // ========================================
  // Batch Enrollment Invitations
  // ========================================

  async listBatchEnrollmentCandidates(): Promise<BatchEnrollmentCandidateVM[]> {
    const { data } = await this._instance.get<BatchEnrollmentCandidateVM[]>(`${this._route}/batch-enrollment-candidates`);
    return data;
  }

  async approveBatchEnrollmentInvitation(invitationId: string): Promise<void> {
    await this._instance.post(`${this._route}/batch-enrollment-invitations/${invitationId}:approve`);
  }

  async rejectBatchEnrollmentInvitation(invitationId: string, rejectionReason: string): Promise<void> {
    await this._instance.post(`${this._route}/batch-enrollment-invitations/${invitationId}:reject`, { rejectionReason });
  }

  // ========================================
  // Batch Participation Payments
  // ========================================

  async approvePayment(body: ApproveBatchParticipationPaymentBody): Promise<void> {
    await this._instance.post(`${this._route}/payments/${body.paymentId}:approve`, body);
  }

  async refusePayment(body: RefuseBatchParticipationPaymentBody): Promise<void> {
    await this._instance.post(`${this._route}/payments/${body.paymentId}:refuse`, body);
  }

  async setEnrollmentBalanceBypass(enrollmentId: string, enabled: boolean): Promise<void> {
    await this._instance.post(`${this._route}/batch-enrollments/${enrollmentId}:set-balance-bypass`, { enabled });
  }

  // ========================================
  // Studios
  // ========================================

  async listStudios(): Promise<AdminStudioListItem[]> {
    const { data } = await this._instance.get<AdminStudioListItem[]>(`${this._route}/studios`);
    return data;
  }

  async getStudioById(id: string): Promise<AdminStudioListItem> {
    const { data } = await this._instance.get<AdminStudioListItem>(`${this._route}/studios/${id}`);
    return data;
  }

  async createStudio(body: CreateAdminStudioBody): Promise<string> {
    const { data } = await this._instance.post(`${this._route}/studios`, body);
    return data;
  }

  async updateStudio(body: UpdateAdminStudioBody): Promise<void> {
    await this._instance.put(`${this._route}/studios/${body.id}`, body);
  }

  // ========================================
  // User Management
  // ========================================

  async impersonate(targetUserId: string, activeRole: ROLES): Promise<ImpersonateResponseVM> {
    const { data } = await this._instance.get<ImpersonateResponseVM>(`${this._route}/users/${targetUserId}:impersonate`, {
      params: { activeRole },
    });
    return data;
  }

  async retrieveUnsubscribedUserList(): Promise<UserVM[]> {
    const { data } = await this._instance.get(`${this._route}/users?isSubscribed=false`);
    return data;
  }

  async retrieveUserList(): Promise<UserVM[]> {
    const { data } = await this._instance.get(`${this._route}/users`);
    return data;
  }

  async getImpersonationCandidates(role: ROLES): Promise<ImpersonationCandidateVM[]> {
    const { data } = await this._instance.get<ImpersonationCandidateVM[]>(`${this._route}/impersonation-candidates`, {
      params: { role },
    });
    return data;
  }

  async getUser(userId: string): Promise<UserDetailVM> {
    const { data } = await this._instance.get(`${this._route}/users/${userId}`);
    return data;
  }

  async adminList(): Promise<AdminVM[]> {
    const { data } = await this._instance.get(`${this._route}/admins`);
    return data;
  }

  // ========================================
  // Funding Requests
  // ========================================

  async adminFundingRequestEligibility(studentId: string): Promise<FundingRequestEligibilityVM> {
    const { data: fundingRequestEligibility } = await this._instance.get(`v1/admin/students/${studentId}/funding-requests/eligibility`);
    return fundingRequestEligibility;
  }

  async getAll(filters?: {
    status: FUNDING_REQUEST_STATUS[];
    funders?: string[];
    trainingItemTypes?: TRAINING_ITEM_TYPE[];
  }): Promise<FundingRequestListItemVM> {
    const queryParams: string[] = [];
    if (filters?.status?.length) {
      queryParams.push(...filters.status.map(s => `status=${encodeURIComponent(s)}`));
    }
    if (filters?.funders?.length) {
      queryParams.push(...filters.funders.map(f => `funders=${encodeURIComponent(f)}`));
    }
    if (filters?.trainingItemTypes?.length) {
      queryParams.push(...filters.trainingItemTypes.map(type => `training-item-types=${encodeURIComponent(type)}`));
    }
    const queryParameter = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
    const { data: fundingRequests } = await this._instance.get(this._adminFundingRequestRoute + queryParameter);
    return fundingRequests;
  }

  async adminGetFundingList(params: FundingListPaginationParams): Promise<PaginatedResult<FundingListItemVM>> {
    const { page = 1, pageSize = 50, sortBy, sortOrder, filters } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(sortBy && { sortBy }),
      ...(sortOrder && { sortOrder }),
      ...(filters?.searchTerm && { searchTerm: filters.searchTerm }),
    });
    const { data: paginatedFundings } = await this._instance.get(`v1/admin/fundings?${queryParams}`);
    return paginatedFundings;
  }

  async review(fundingRequestId: string, fundingRequest: ReviewFundingRequestBody): Promise<void> {
    await this._instance.post(`${this._adminFundingRequestRoute}/${fundingRequestId}:review`, fundingRequest);
  }

  async get(fundingRequestId: string): Promise<FundingRequestVM> {
    const { data: fundingRequest } = await this._instance.get(`${this._adminFundingRequestRoute}/${fundingRequestId}`);
    return fundingRequest;
  }

  async getApproved(fundingRequestId: string): Promise<ApprovedFundingRequestDetailVM> {
    const { data: fundingRequest } = await this._instance.get(`${this._adminFundingRequestRoute}/${fundingRequestId}:approved`);
    return fundingRequest;
  }

  async delete(fundingRequestId: string): Promise<void> {
    await this._instance.delete(`${this._adminFundingRequestRoute}/${fundingRequestId}`);
  }

  async submitToFunder(fundingRequestId: string, body: { hasBeenSubmitted: boolean }): Promise<void> {
    await this._instance.post(`${this._adminFundingRequestRoute}/${fundingRequestId}:submit-to-funder`, body);
  }

  async updateErrorReason(
    fundingRequestId: string,
    body: { error: FUNDING_REQUEST_ERROR_REASONS; sendNotificationToStudent: boolean },
  ): Promise<void> {
    await this._instance.post(`${this._adminFundingRequestRoute}/${fundingRequestId}:update-error-reason`, body);
  }

  async revertFundingRequestToReview(fundingRequestId: string): Promise<void> {
    await this._instance.post(`${this._adminFundingRequestRoute}/${fundingRequestId}:revert`);
  }

  async updateFundingRequest(fundingRequest: UpdateFundingRequestBody): Promise<void> {
    await this._instance.put(`${this._adminFundingRequestRoute}/${fundingRequest.id}`, fundingRequest);
  }

  async approve(fundingRequest: ApproveFundingRequestBody): Promise<void> {
    await this._instance.post(`${this._adminFundingRequestRoute}/${fundingRequest.fundingRequestId}:approve`, fundingRequest);
  }

  async cancelFunding(fundingRequestId: string): Promise<void> {
    await this._instance.post(`${this._adminFundingRequestRoute}/${fundingRequestId}:cancel`);
  }

  async getEligibleTrainingItems(studentId: string): Promise<EligibleTrainingItemsVM> {
    const { data } = await this._instance.get(`${this._route}/students/${studentId}/funding-request-eligible-training-items`);
    return data;
  }

  // ========================================
  // Payouts
  // ========================================

  async getAdminTeacherPayouts(params: TeacherPayoutListPaginationParams): Promise<PaginatedResult<AdminTeacherPayoutVM>> {
    const { page = 1, pageSize = 50, sortBy, sortOrder, filters } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(sortBy && { sortBy }),
      ...(sortOrder && { sortOrder }),
      ...(filters?.searchTerm && { searchTerm: filters.searchTerm }),
    });
    if (filters?.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach(status => {
        queryParams.append("statuses", status);
      });
    }
    const { data: paginatedPayouts } = await this._instance.get(`${this._route}/teacher-payouts?${queryParams}`);
    return paginatedPayouts;
  }

  async validateTeacherPayout(command: ValidateTeacherPayoutBody): Promise<void> {
    await this._instance.put(`${this._route}/teacher-payouts/${command.payoutId}:validate`);
  }

  async replaceTeacherPayoutInvoice(payoutId: string, invoiceUrl: string): Promise<void> {
    await this._instance.put(`${this._route}/teacher-payouts/${payoutId}:replace`, { invoiceUrl });
  }

  async replaceProviderPayoutInvoice(payoutId: string, invoiceUrl: string): Promise<void> {
    await this._instance.put(`${this._route}/payments/${payoutId}:replace`, { invoiceUrl });
  }

  // ========================================
  // Lessons
  // ========================================

  async listLessons(params: LessonPaginationParams): Promise<PaginatedResult<AdminLessonListVM>> {
    const { page = 1, pageSize = 50, sortBy, sortOrder, filters } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(sortBy && { sortBy }),
      ...(sortOrder && { sortOrder }),
      ...(filters?.searchTerm && { searchTerm: filters.searchTerm }),
    });
    const { data: lessons } = await this._instance.get(`${this._route}/lessons?${queryParams}`);
    return lessons;
  }

  async teacherLessons(teacherId: string): Promise<TeacherLessonListVM[]> {
    const { data: lessons } = await this._instance.get(`${this._route}/teachers/${teacherId}/lessons`);
    return lessons;
  }

  // ========================================
  // Student Management
  // ========================================

  async getStudent(id: string): Promise<AdminStudentVM> {
    const { data: student } = await this._instance.get(`${this._route}/students/${id}`);
    return student;
  }

  async listStudents(): Promise<StudentListElementVM[]> {
    const { data: students } = await this._instance.get(`${this._route}/students`);
    return students;
  }

  async globalSearch(query: string): Promise<AdminGlobalSearchVM> {
    const { data } = await this._instance.get(`${this._route}/search`, { params: { query } });
    return data;
  }

  async updateUserAccount(userId: string, body: UpdateUserAccountBody): Promise<void> {
    await this._instance.put(`${this._route}/users/${userId}`, body);
  }

  async updateStudentSettings(studentId: string, body: UpdateStudentSettingsBody): Promise<void> {
    await this._instance.put(`${this._route}/students/${studentId}`, body);
  }

  async getStudentSummary(id: string): Promise<StudentSummaryVM> {
    const { data: summary } = await this._instance.get(`${this._route}/students/${id}/summary`);
    return summary;
  }

  // ========================================
  // Teacher Management
  // ========================================

  async getTeacher(id: string): Promise<AdminTeacherVM> {
    const { data: teacher } = await this._instance.get(`${this._route}/teachers/${id}`);
    return teacher;
  }

  async listTeachers(): Promise<TeacherListElementVM[]> {
    const { data: teachers } = await this._instance.get(`${this._route}/teachers`);
    return teachers;
  }

  async updateTeacherSettings(teacherId: string, body: UpdateTeacherSettingsBody): Promise<void> {
    await this._instance.put(`${this._route}/teachers/${teacherId}`, body);
  }

  // ========================================
  // Provider Management
  // ========================================

  async listProviders(): Promise<ProviderListItemVM[]> {
    const { data: providers } = await this._instance.get(`${this._route}/providers`);
    return providers;
  }

  async getProvider(id: string): Promise<AdminProviderVM> {
    const { data: provider } = await this._instance.get(`${this._route}/providers/${id}`);
    return provider;
  }

  async updateProviderSettings(providerId: string, body: UpdateProviderSettingsBody): Promise<void> {
    await this._instance.put(`${this._route}/providers/${providerId}`, body);
  }

  // ========================================
  // Transfers
  // ========================================

  async listTransfers(): Promise<StandardPayment[]> {
    const { data: transfers } = await this._instance.get(`${this._route}/payments`);
    return transfers;
  }

  async listConventionTransfers(): Promise<ConventionPaymentVM[]> {
    const { data: transfers } = await this._instance.get(`${this._route}/payments/conventions`);
    return transfers;
  }

  async updateTransfer(id: string, transfer: UpdatePaymentBody): Promise<string> {
    const { data: transfers } = await this._instance.put(`${this._route}/payments/${id}`, transfer);
    return transfers;
  }

  async listTransferLessons(id: string): Promise<LessonListVM> {
    const { data: lessons } = await this._instance.get(`${this._route}/payments/${id}/lessons`);
    return lessons;
  }

  // ========================================
  // Internal Trainings
  // ========================================

  async listInternalTrainings(): Promise<InternalTrainingVM[]> {
    const { data: trainings } = await this._instance.get(`${this._route}/internal-trainings`);
    return trainings;
  }

  async createInternalTraining(training: AdminCreateInternalTrainingBody): Promise<string> {
    const { data } = await this._instance.post(`${this._route}/internal-trainings`, training);
    return data;
  }

  async updateInternalTraining(training: AdminUpdateInternalTrainingBody): Promise<void> {
    await this._instance.put(`${this._route}/internal-trainings/${training.trainingId}`, training);
  }

  async deleteInternalTraining(trainingId: string): Promise<void> {
    await this._instance.delete(`${this._route}/internal-trainings/${trainingId}`);
  }

  // ========================================
  // Documentary Resources
  // ========================================

  async getDocumentaryResourcesCatalog(): Promise<AdminDocumentaryResourceVM[]> {
    const { data: resources } = await this._instance.get(`${this._route}/documentary-resources`);
    return resources;
  }

  async createDocumentaryResource(body: CreateDocumentaryResourceBody): Promise<string> {
    const { data: resource } = await this._instance.post(`${this._route}/documentary-resources`, body);
    return resource.id;
  }

  async updateDocumentaryResource(body: UpdateDocumentaryResourceBody): Promise<void> {
    const { resourceId, ...payload } = body;
    await this._instance.put(`${this._route}/documentary-resources/${resourceId}`, payload);
  }

  async archiveDocumentaryResource(resourceId: string): Promise<void> {
    await this._instance.post(`${this._route}/documentary-resources/${resourceId}/archive`);
  }

  // ========================================
  // Refunds
  // ========================================

  async listRefunds(): Promise<RefundVM[]> {
    const { data: refunds } = await this._instance.get(`${this._route}/refunds`);
    return refunds;
  }

  async updateRefund(id: string, refund: UpdateRefundBody): Promise<void> {
    await this._instance.put(`${this._route}/refunds/${id}`, refund);
  }

  // ========================================
  // Balance Adjustments
  // ========================================

  async listBalanceAdjustments(): Promise<AdminBalanceAdjustmentListVM[]> {
    const { data: balanceAdjustments } = await this._instance.get(`${this._route}/balance-adjustments`);
    return balanceAdjustments;
  }

  async createBalanceAdjustment(balanceAdjustment: CreateBalanceAdjustmentBody): Promise<string> {
    const { data: balanceAdjustmentId } = await this._instance.post(
      `${this._route}/students/${balanceAdjustment.studentId}/balance-adjustments`,
      balanceAdjustment,
    );
    return balanceAdjustmentId;
  }

  // ========================================
  // Timeline Events
  // ========================================

  async listTimelineEvents(targetedUserId: string): Promise<TimelineEventVM[]> {
    const { data: timelineEvents } = await this._instance.post(`${this._route}/timeline-events:search`, { targetedUserId });
    return timelineEvents;
  }

  async createTimelineEvent(event: AddTimelineEventBody): Promise<void> {
    await this._instance.post(`${this._route}/timeline-events`, event);
  }

  async updateTimelineEvent(event: UpdateTimelineEventBody): Promise<void> {
    await this._instance.put(`${this._route}/timeline-events/${event.timelineEventId}`, event);
  }

  // ========================================
  // Unified User Creation
  // ========================================

  async createUser(body: CreateUserBody): Promise<string> {
    const { data: userId } = await this._instance.post(`${this._route}/users`, body);
    return userId;
  }

  async updateRoles(userId: string, body: UpdateRolesBody): Promise<void> {
    await this._instance.put(`${this._route}/users/${userId}/roles`, body);
  }

  // ========================================
  // User Invitations
  // ========================================

  async refreshUserInvitation(userId: string): Promise<void> {
    await this._instance.post(`${this._route}/users/${userId}/invitations:refresh`);
  }

  async listUserInvitations(): Promise<UserInvitationVM[]> {
    const { data: invitations } = await this._instance.get(`${this._route}/accounts/invitations`);
    return invitations;
  }

  async listUninvitedUsers(): Promise<UninvitedUserVM[]> {
    const { data: uninvitedUsers } = await this._instance.get(`${this._route}/accounts/uninvited`);
    return uninvitedUsers;
  }

  async sendUserInvitation(userId: string): Promise<void> {
    await this._instance.post(`${this._route}/users/${userId}/invitations:send`);
  }

  // ========================================
  // Training Follow-Ups
  // ========================================

  async adminGetAll(params: TrainingFollowUpPaginationParams): Promise<PaginatedResult<TrainingFollowUpWithUrgenciesVM>> {
    const { page = 1, pageSize = 50, sortBy, sortOrder, filters } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(sortBy && { sortBy }),
      ...(sortOrder && { sortOrder }),
      ...(filters?.searchTerm && { searchTerm: filters.searchTerm }),
      ...(filters?.organization && { organization: filters.organization }),
      ...(filters?.minHoursCompletionRatio !== undefined && { minHoursCompletionRatio: filters.minHoursCompletionRatio.toString() }),
    });
    if (filters?.includedStatuses && filters.includedStatuses.length > 0) {
      filters.includedStatuses.forEach(status => {
        queryParams.append("includedStatuses", status);
      });
    }
    if (filters?.adminIds && filters.adminIds.length > 0) {
      filters.adminIds.forEach(adminId => {
        queryParams.append("adminIds", adminId);
      });
    }
    const { data: paginatedTrainings } = await this._instance.get(`${this._route}/training-follow-ups?${queryParams}`);
    return paginatedTrainings;
  }

  async adminGetOne(id: string): Promise<AdminTrainingFollowUpVM> {
    const { data: training } = await this._instance.get(`${this._route}/training-follow-ups/${id}`);
    return training;
  }

  async changeTrainingFollowUpEndDate({ trainingFollowUpId, endDate }: { trainingFollowUpId: string; endDate: Date }): Promise<void> {
    await this._instance.post(`${this._route}/training-follow-ups/${trainingFollowUpId}:change-end-date`, {
      trainingFollowUpId,
      endDate,
    });
  }

  async advanceTrainingFollowUpEndDateToHourQuota(trainingFollowUpId: string): Promise<void> {
    await this._instance.post(`${this._route}/training-follow-ups/${trainingFollowUpId}:advance-end-date-to-hour-quota`);
  }

  async getAttendanceSheets(trainingId: string): Promise<TrainingFollowUpAttendanceSheetVM[]> {
    const { data: attendanceSheets } = await this._instance.get(`${this._route}/training-follow-ups/${trainingId}/attendance-sheets`);
    return attendanceSheets;
  }

  async getTrainingItems(trainingFollowUpId: string): Promise<TrainingFollowUpTrainingItemVM[]> {
    const { data: trainingItems } = await this._instance.get(`${this._route}/training-follow-ups/${trainingFollowUpId}/training-items`);
    return trainingItems;
  }

  async backfillAttendanceSheets(trainingId: string): Promise<void> {
    await this._instance.post(`${this._route}/training-follow-ups/${trainingId}:backfill-attendance-sheets`);
  }

  // ========================================
  // Training Invoices
  // ========================================

  async listTrainingInvoices(params: TrainingInvoiceListPaginationParams): Promise<PaginatedResult<TrainingInvoice>> {
    const { page = 1, pageSize = 50, sortBy, sortOrder, filters } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(sortBy && { sortBy }),
      ...(sortOrder && { sortOrder }),
      ...(filters?.searchTerm && { searchTerm: filters.searchTerm }),
    });
    if (filters?.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach(status => {
        queryParams.append("statuses", status);
      });
    }
    const { data: paginatedInvoices } = await this._instance.get(`${this._route}/training-invoices?${queryParams}`);
    return paginatedInvoices;
  }

  async createCustomerInvoice(trainingInvoiceId: string): Promise<void> {
    await this._instance.post(`${this._route}/training-invoices/${trainingInvoiceId}:create-customer-invoice`);
  }
}
